import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Clock, Mail, CheckCircle2 } from "lucide-react";

export default function RefundPolicy() {
  return (
    <div className="min-h-screen bg-white" data-testid="refund-policy-page">
      <header className="border-b border-[#E4E4E7] px-6 py-4 sticky top-0 bg-white/80 backdrop-blur-md z-50">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <Link to="/" className="text-[#A1A1AA] hover:text-[#09090B] transition-colors">
            <ArrowLeft size={20} />
          </Link>
          <h1 className="font-display font-bold text-[#09090B] text-lg">Refund Policy</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        {/* Hero Badge */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#FF7300]/10 mb-4">
            <Shield size={32} className="text-[#FF7300]" />
          </div>
          <h2 className="font-display font-extrabold text-[#09090B] text-3xl sm:text-4xl tracking-tight mb-3">
            30-Day Money-Back Guarantee
          </h2>
          <p className="text-[#52525B] text-base md:text-lg max-w-lg mx-auto">
            We stand behind our product. If you're not satisfied, we'll refund your purchase — no questions asked.
          </p>
        </div>

        {/* Key Points */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-12">
          <div className="border border-[#E4E4E7] rounded-xl p-5 text-center">
            <Clock size={24} className="text-[#FF7300] mx-auto mb-3" />
            <h3 className="font-display font-bold text-[#09090B] text-sm mb-1">30 Days</h3>
            <p className="text-xs text-[#52525B]">Full refund window from date of purchase</p>
          </div>
          <div className="border border-[#E4E4E7] rounded-xl p-5 text-center">
            <CheckCircle2 size={24} className="text-[#067d06] mx-auto mb-3" />
            <h3 className="font-display font-bold text-[#09090B] text-sm mb-1">No Questions</h3>
            <p className="text-xs text-[#52525B]">Simply request and we'll process your refund</p>
          </div>
          <div className="border border-[#E4E4E7] rounded-xl p-5 text-center">
            <Mail size={24} className="text-[#2563EB] mx-auto mb-3" />
            <h3 className="font-display font-bold text-[#09090B] text-sm mb-1">Easy Process</h3>
            <p className="text-xs text-[#52525B]">Email us and receive your refund within 5-7 days</p>
          </div>
        </div>

        {/* Policy Content */}
        <div className="prose-style space-y-8">
          <section>
            <h3 className="font-display font-bold text-[#09090B] text-lg mb-3">Eligibility</h3>
            <p className="text-sm text-[#52525B] leading-relaxed">
              All purchases of Amazon Affiliate PRO are covered by our 30-day money-back guarantee. If you are not completely satisfied with your purchase for any reason, you may request a full refund within 30 calendar days of the original purchase date.
            </p>
          </section>

          <section>
            <h3 className="font-display font-bold text-[#09090B] text-lg mb-3">How to Request a Refund</h3>
            <ol className="list-decimal list-inside space-y-2 text-sm text-[#52525B] leading-relaxed">
              <li>Send an email to <a href="mailto:info@amznawp.com" className="text-[#FF7300] font-semibold hover:underline">info@amznawp.com</a></li>
              <li>Include your PayPal transaction ID or order confirmation email</li>
              <li>Briefly describe the reason for your refund (optional but helpful)</li>
              <li>We'll process your refund within 5-7 business days</li>
            </ol>
          </section>

          <section>
            <h3 className="font-display font-bold text-[#09090B] text-lg mb-3">Refund Process</h3>
            <p className="text-sm text-[#52525B] leading-relaxed">
              Once we receive your refund request, we will verify your purchase and process the refund to your original payment method (PayPal or credit/debit card). Refunds typically appear in your account within 5-7 business days, though this may vary depending on your financial institution.
            </p>
          </section>

          <section>
            <h3 className="font-display font-bold text-[#09090B] text-lg mb-3">After a Refund</h3>
            <p className="text-sm text-[#52525B] leading-relaxed">
              Upon processing your refund, your license key will be deactivated. You should uninstall the PRO plugin from your WordPress site(s). You may continue using the Free version at any time without restriction.
            </p>
          </section>

          <section>
            <h3 className="font-display font-bold text-[#09090B] text-lg mb-3">Subscription Renewals</h3>
            <p className="text-sm text-[#52525B] leading-relaxed">
              Your subscription will auto-renew yearly. You can cancel at any time before the renewal date. If you are charged for an unwanted renewal, contact us within 30 days of the charge for a full refund of the renewal payment.
            </p>
          </section>

          <section>
            <h3 className="font-display font-bold text-[#09090B] text-lg mb-3">Contact Us</h3>
            <p className="text-sm text-[#52525B] leading-relaxed">
              For any questions about this refund policy or your purchase, contact us at{" "}
              <a href="mailto:info@amznawp.com" className="text-[#FF7300] font-semibold hover:underline">info@amznawp.com</a>.
              We typically respond within 24 hours on business days.
            </p>
          </section>
        </div>

        {/* Back to Home */}
        <div className="mt-12 pt-8 border-t border-[#E4E4E7] text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#FF7300] hover:underline"
          >
            <ArrowLeft size={14} /> Back to Home
          </Link>
        </div>
      </main>
    </div>
  );
}
